import type { TenantSlug } from "@/lib/theme-presets";

const PRODUCTION_TENANT_HOSTS: Readonly<Record<string, TenantSlug>> = {
  "murtadaacademy.org": "murtada",
  "portal.mufidseminary.org": "mufid",
};

const DEVELOPMENT_TENANT_HOSTS: Readonly<Record<string, TenantSlug>> = {
  localhost: "murtada",
  "murtada.localhost": "murtada",
  "mufid.localhost": "mufid",
};

function developmentRemoteTenantHost(): readonly [string, TenantSlug] | null {
  if (!import.meta.env.DEV) return null;

  const hostname = import.meta.env.VITE_DEV_REMOTE_HOST?.trim().toLowerCase().replace(/\.$/, "");
  const tenant = import.meta.env.VITE_DEV_TENANT_SLUG?.trim().toLowerCase();
  if (!hostname || (tenant !== "murtada" && tenant !== "mufid")) return null;
  return [hostname, tenant];
}

interface ReleaseHostnameEntry {
  host: string;
  tenant_key: TenantSlug;
}

interface ReleaseHostnameRegistry {
  version: string;
  hosts: ReleaseHostnameEntry[];
}

function decodeRegistryHex(value: string): unknown {
  if (!value || value.length % 2 !== 0 || !/^[0-9a-f]+$/.test(value)) {
    throw new Error("Release hostname registry must be lowercase hexadecimal");
  }
  const bytes = new Uint8Array(value.length / 2);
  for (let index = 0; index < value.length; index += 2) {
    bytes[index / 2] = Number.parseInt(value.slice(index, index + 2), 16);
  }
  return JSON.parse(new TextDecoder().decode(bytes)) as unknown;
}

export function buildReleaseTenantHosts(
  releaseEnvironment: string | undefined,
  registryVersion: string | undefined,
  registryHex: string | undefined,
): Readonly<Record<string, TenantSlug>> {
  if (releaseEnvironment === undefined) {
    return {};
  }
  if (releaseEnvironment !== "production") {
    throw new Error("Invalid release environment");
  }
  const value = decodeRegistryHex(registryHex ?? "");
  if (typeof value !== "object" || value === null) {
    throw new Error("Invalid release hostname registry");
  }
  const registry = value as Partial<ReleaseHostnameRegistry>;
  if (
    typeof registry.version !== "string"
    || !registry.version
    || registry.version !== registryVersion
    || !Array.isArray(registry.hosts)
    || registry.hosts.length !== 2
    || Object.keys(value).some((key) => !["version", "hosts"].includes(key))
  ) {
    throw new Error("Invalid release hostname registry");
  }
  const result: Record<string, TenantSlug> = {};
  for (const entry of registry.hosts) {
    if (
      typeof entry !== "object"
      || entry === null
      || Object.keys(entry).some((key) => !["host", "tenant_key"].includes(key))
      || typeof entry.host !== "string"
      || entry.host !== entry.host.toLowerCase()
      || !/^[a-z0-9](?:[a-z0-9.-]{0,251}[a-z0-9])?$/.test(entry.host)
      || (entry.tenant_key !== "murtada" && entry.tenant_key !== "mufid")
      || result[entry.host] !== undefined
      || Object.values(result).includes(entry.tenant_key)
    ) {
      throw new Error("Invalid release hostname registry");
    }
    result[entry.host] = entry.tenant_key;
  }
  if (new Set(Object.values(result)).size !== 2) {
    throw new Error("Invalid release hostname registry");
  }
  return result;
}

const RELEASE_ENVIRONMENT = import.meta.env.VITE_RELEASE_ENVIRONMENT;
const RELEASE_TENANT_HOSTS = buildReleaseTenantHosts(
  RELEASE_ENVIRONMENT,
  import.meta.env.VITE_RELEASE_HOSTNAME_REGISTRY_VERSION,
  import.meta.env.VITE_RELEASE_HOSTNAME_REGISTRY_HEX,
);

export function resolveTenantSlugFromRegistry(
  hostname: string,
  releaseEnvironment: string | undefined,
  releaseTenantHosts: Readonly<Record<string, TenantSlug>>,
  development: boolean,
  developmentRemoteHost: readonly [string, TenantSlug] | null = null,
): TenantSlug | null {
  const normalized = hostname.trim().toLowerCase().replace(/\.$/, "");
  if (releaseEnvironment === "production") {
    return releaseTenantHosts[normalized] ?? null;
  }
  return (
    PRODUCTION_TENANT_HOSTS[normalized]
    ?? (development ? DEVELOPMENT_TENANT_HOSTS[normalized] : undefined)
    ?? (
      development && developmentRemoteHost?.[0] === normalized
        ? developmentRemoteHost[1]
        : undefined
    )
    ?? null
  );
}

/** Exact hostname lookup. Unknown, prefix, suffix, and preview hosts return no tenant. */
export function tenantSlugFromHostname(
  hostname: string = window.location.hostname,
): TenantSlug | null {
  return resolveTenantSlugFromRegistry(
    hostname,
    RELEASE_ENVIRONMENT,
    RELEASE_TENANT_HOSTS,
    import.meta.env.DEV,
    developmentRemoteTenantHost(),
  );
}

/**
 * Human display name for a tenant slug — the sanctioned synchronous fallback for
 * UI that needs a tenant name before the server-driven name (`GET /tenant[/me]`)
 * resolves. This lives here, in the tenant-resolution layer (exempt from the
 * `no-restricted-syntax` slug-literal guard), so consumers never branch on the
 * slug themselves. Prefer the server name from `useTenantConfig().name`; use this
 * only as the pre-resolution fallback. Unknown slugs return no profile.
 */
export interface TenantStaticProfile {
  displayName: string;
  logoUrl: string | null;
  sidebarLogoUrl: string | null;
  parentOrg: string | null;
  /**
   * Domain of the managed Workspace accounts this tenant owns and provisions.
   * Surfaces that name a managed account read it from here rather than writing
   * a literal, because those surfaces are shared by both tenants and a literal
   * silently addresses the wrong institution on the other one.
   */
  managedAccountDomain: string;
}

const TENANT_STATIC_PROFILES: Record<string, TenantStaticProfile> = {
  murtada: {
    displayName: "Murtada Academy",
    logoUrl: "/brand/murtada-logo.png",
    sidebarLogoUrl: "/brand/murtada/logo-mark-green.png",
    parentOrg: "A program of IMAM",
    managedAccountDomain: "murtadaacademy.org",
  },
  mufid: {
    displayName: "Mufid Seminary",
    logoUrl: "/brand/mufid-logo.png",
    sidebarLogoUrl: "/brand/mufid-logo.png",
    parentOrg: null,
    managedAccountDomain: "mufidseminary.org",
  },
};

export function tenantDisplayName(slug: string | null | undefined): string | null {
  return tenantStaticProfile(slug)?.displayName ?? null;
}

/** Hostname-registry fallback used only before public branding resolves or after it fails. */
export function tenantStaticProfile(
  slug: string | null | undefined,
): TenantStaticProfile | null {
  return slug ? TENANT_STATIC_PROFILES[slug] ?? null : null;
}
